import * as R from 'ramda';
import { throttle } from 'throttle-debounce';

import { getFloorAtPrecise, getStructureAndFloorAtPoint } from '@utils/geom.ts';

import type {
  Id,
  MapFeature,
  MapGeoJsonSource,
  MapInitialized,
  MapRendererApp,
  StructureState,
  VenueFloor,
  VenueMapData,
  VenueStructure,
} from './types.ts';
import { setSource, createMapFeature } from './utils.ts';

const getStructureLevels = (structure: VenueStructure): VenueFloor[] => {
  const levels = Array.isArray(structure.levels) ? structure.levels : Object.values(structure.levels ?? {});
  return levels.filter((level): level is VenueFloor => level != null);
};

export default function FloorController(app: MapRendererApp, mapInitialized: MapInitialized) {
  const ORDINAL_SOURCE = 'llOrdinalSource';
  const BACKGROUND_SOURCE = 'llBackgroundSource';

  let state: StructureState = {
    currentOrdinal: null,
    floors: [],
    structures: [],
    currentFloorId: null,
    currentStructureId: null,
  };

  const featureThemer = app.bus.get('map/getMapFeatureThemer');

  const initMapSources = (sources: Record<string, MapGeoJsonSource>): Record<string, MapGeoJsonSource> => {
    return {
      ...sources,
      [BACKGROUND_SOURCE]: createMapFeature([]),
      [ORDINAL_SOURCE]: createMapFeature([]),
    };
  };

  mapInitialized().then(map => {
    dispatchChangeFloorIfNeeded(map);
    const throttledCheckFloor = throttle(500, dispatchChangeFloorIfNeeded);

    map.on('move', (e: { originalEvent?: unknown }) => {
      // only consider dispatching level change if the event was user interaction, not programmatic
      if (e.originalEvent) {
        throttledCheckFloor(map, true);
      }
    });

    map.on('moveend', () => dispatchChangeFloorIfNeeded(map));
  });

  function dispatchChangeFloor(floor: VenueFloor | null, structure: VenueStructure | null, userInitiated: boolean) {
    const newStructureId = structure ? structure.id : null;
    const newFloorId = floor ? floor.id : null;
    if (state.currentStructureId !== newStructureId) {
      app.bus.send('map/structureChanged', { structure, floor, userInitiated });
    }
    app.bus.send('map/floorChanged', { structure, floor });
    state = {
      ...state,
      currentFloorId: newFloorId,
      currentStructureId: newStructureId,
    };
  }

  const dispatchChangeFloorIfNeeded = (map: Awaited<ReturnType<MapInitialized>>, userInitiated = false) => {
    const { structures, currentOrdinal } = state;
    const { lat, lng } = map.getCenter();
    const mbp = map.getBounds(); // map bounds provider
    const mapviewBBox = [mbp.getWest(), mbp.getSouth(), mbp.getEast(), mbp.getNorth()];

    const { structure, floor } = getStructureAndFloorAtPoint(
      structures,
      lat,
      lng,
      currentOrdinal ?? 0,
      mapviewBBox,
    ) as {
      structure: VenueStructure | null;
      floor: VenueFloor | null;
    };

    const structureOrFloorChanged = floor?.id != state.currentFloorId || structure?.id != state.currentStructureId; // use non-strict to handle null/undefined as same
    if (structureOrFloorChanged) {
      dispatchChangeFloor(floor, structure, userInitiated);
    }
  };

  const getStructure = (structureId: Id): VenueStructure | undefined =>
    state.structures.find(structure => structure.id === structureId);

  const getFloor = (floorId: Id): VenueFloor | undefined => state.floors.find(floor => floor.id === floorId);

  const getStructureForFloorId = (floorId: Id): VenueStructure | undefined =>
    state.structures.find(structure => getStructureLevels(structure).some(level => level.id === floorId));

  // Reposition the map to focus (select) the specified structure. If
  // changeToDefaultFloor is true (default), change ord to this building's default floor.
  const selectStructure = (structureId: Id, changeToDefaultFloor = true) => {
    const structure = getStructure(structureId);
    if (!structure) {
      throw Error(`Unknown structure ID ${structureId}`);
    }
    let { currentFloorId } = state;
    if (changeToDefaultFloor && structure.defaultLevelId != null) {
      changeFloor(structure.defaultLevelId);
      currentFloorId = structure.defaultLevelId; // tempting to write to state here...
    }

    app.bus.send('map/centerInBounds', {
      bounds: (currentFloorId != null ? getFloor(currentFloorId)?.bounds : undefined) || structure.bounds,
    });
  };

  // Reposition the map to focus (select) the specified floor.
  const selectFloor = (id: Id) => {
    const structure = getStructureForFloorId(id);
    if (!structure) {
      throw Error(`Unknown floor ${id}.`);
    }
    app.bus.send('map/centerInBounds', { bounds: structure.bounds });
    changeFloor(id);
  };

  // switch the ordinal to match that of the selected floor. Does not
  // move or zoom the map unless centerMap is true
  const changeFloor = (floorId: Id, centerMap = false) => {
    const floor = getFloor(floorId);
    if (!floor) {
      throw Error(`Unknown floor ${floorId}.`);
    }
    const ordinal = floor.ordinal;
    changeOrdinal(ordinal);
    if (centerMap) {
      app.bus.send('map/centerInBounds', { bounds: floor.bounds });
    }
  };

  const changeOrdinal = (ordinal: number | null | undefined) => {
    mapInitialized().then(map => {
      if (ordinal == null || ordinal === state.currentOrdinal) {
        return;
      }
      state = { ...state, currentOrdinal: ordinal };
      if (!state.ordinalFeatureSets?.[ordinal]) {
        console.warn(`Trying to display ordinal '${ordinal}' but no source found for it!`, state.ordinalFeatureSets);
      }
      setSource(ORDINAL_SOURCE, state.ordinalFeatureSets?.[ordinal] ?? [], map);
      dispatchChangeFloorIfNeeded(map);
      app.bus.send('map/ordinalChanged', { ordinal });
    });
  };

  app.bus.on('venueData/mapDataLoaded', async (params: VenueMapData) => {
    const { structures, mapFeatures, defaultOrdinal, venueId } = params;
    const floors = structures.flatMap(getStructureLevels);
    const themer = (await featureThemer) as (feature: MapFeature) => MapFeature;
    const backgroundFeatures = Array.isArray(mapFeatures?.[String(venueId)])
      ? (mapFeatures[String(venueId)] as MapFeature[]).map(themer)
      : [];
    mapInitialized().then(map => {
      setSource(BACKGROUND_SOURCE, backgroundFeatures, map);
      app.bus.send('map/mapReadyToShow', { map });
    });
    const ordinalFeatureSets: Record<number, MapFeature[]> = {};
    floors.forEach(floor => {
      if (!ordinalFeatureSets[floor.ordinal]) {
        ordinalFeatureSets[floor.ordinal] = [];
      }
      const themedFeatures = Array.isArray(mapFeatures?.[String(floor.id)])
        ? (mapFeatures[String(floor.id)] as MapFeature[]).map(themer)
        : undefined;
      if (themedFeatures) {
        ordinalFeatureSets[floor.ordinal].push(...themedFeatures);
      }
    });
    state = {
      ...state,
      floors,
      structures,
      ordinalFeatureSets,
      defaultOrdinal,
      currentOrdinal: null,
      currentFloorId: null,
    };
    changeOrdinal(defaultOrdinal);
  });

  // TERMINOLOGY:
  //   select = zoom/position map (Focus)
  //   change = no map repositioning (i.e. changeFloor or changeOrdinal)

  app.bus.on('mapLevelSelector/selectLevel', ({ id, centerMap = false }: { id: Id; centerMap?: boolean }) =>
    changeFloor(id, centerMap),
  );

  // Reposition / Zoom map to "select" (focus) onto the building specified.
  // This is aimed at servicing the levelSelector (hense name) so it also changes
  // the ordinal to match the default floor of the structure.
  // This should probably be renamed to map/selectBuilding.
  app.bus.on(
    'mapLevelSelector/selectBuilding',
    ({ id, changeToDefaultFloor = true }: { id: Id; changeToDefaultFloor?: boolean }) =>
      selectStructure(id, changeToDefaultFloor),
  );

  app.bus.on('map/selectFloor', ({ id }: { id: Id }) => selectFloor(id));

  app.bus.on('map/changeFloor', ({ id }: { id: Id }) => changeFloor(id));

  app.bus.on('map/changeOrdinal', ({ ordinal }: { ordinal: number | null }) => changeOrdinal(ordinal));

  app.bus.on('map/getCurrentOrdinal', () => {
    return state.currentOrdinal;
  });

  app.bus.on('app/reset', () => {
    changeOrdinal(state.defaultOrdinal);
  });

  app.bus.on(
    'map/getFloorAt',
    ({ lat, lng, ordinal = null }: { lat: number; lng: number; ordinal?: number | null }) => {
      const { currentOrdinal, structures } = state;
      const correctOrdinal = ordinal === null ? currentOrdinal : ordinal;
      if (correctOrdinal == null) {
        return null;
      }
      return getFloorAtPrecise(structures, lat, lng, correctOrdinal);
    },
  );

  app.bus.on('map/mutateFeature', ({ functor }: { functor: (feature: MapFeature) => MapFeature }) =>
    mapInitialized().then(async map => {
      const themer = (await featureThemer) as (feature: MapFeature) => MapFeature;
      state.ordinalFeatureSets = R.map(R.map(R.pipe(functor, themer)), state.ordinalFeatureSets ?? {});
      if (state.currentOrdinal !== null) {
        setSource(ORDINAL_SOURCE, state.ordinalFeatureSets[state.currentOrdinal] ?? [], map);
      }
    }),
  );

  const runTest = async (initialState: Partial<StructureState>, testRoutine: () => Promise<void> | void) => {
    state = R.mergeRight(state, initialState);
    await testRoutine();
    return state;
  };

  return { initMapSources, runTest };
}
